import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../services/api';

const AuthContext = createContext();

const initialState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  loading: true,
};

function authReducer(state, action) {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      localStorage.setItem('tokens', JSON.stringify(action.payload.tokens));
      localStorage.setItem('user', JSON.stringify(action.payload.user));
      return {
        ...state,
        user: action.payload.user,
        tokens: action.payload.tokens,
        isAuthenticated: true,
        loading: false,
      };
    case 'TOKENS_UPDATED':
      if (action.payload?.tokens) {
        try { localStorage.setItem('tokens', JSON.stringify(action.payload.tokens)); } catch (_) {}
      }
      return {
        ...state,
        tokens: action.payload?.tokens || state.tokens,
        isAuthenticated: true,
      };
    case 'LOGOUT':
      localStorage.removeItem('tokens');
      localStorage.removeItem('user');
      return {
        ...state,
        user: null,
        tokens: null,
        isAuthenticated: false,
        loading: false,
      };
    case 'LOAD_USER':
      return {
        ...state,
        user: action.payload.user,
        tokens: action.payload.tokens,
        isAuthenticated: true,
        loading: false,
      };
    case 'AUTH_ERROR':
      localStorage.removeItem('tokens');
      localStorage.removeItem('user');
      return {
        ...state,
        user: null,
        tokens: null,
        isAuthenticated: false,
        loading: false,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'UPDATE_USER':
      if (action.payload) {
        localStorage.setItem('user', JSON.stringify(action.payload));
      } else {
        localStorage.removeItem('user');
      }
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
}

// Безопасный декодер payload JWT (base64url -> JSON)
function decodeJwtPayload(token) {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '==='.slice((base64.length + 3) % 4);
    const json = atob(padded);
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch (e) {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const refreshToken = useCallback(async (refreshTokenValue) => {
    try {
      const newTokens = await apiService.refreshTokensSingleFlight(refreshTokenValue);
      const user = JSON.parse(localStorage.getItem('user'));
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user,
          tokens: newTokens,
        },
      });
    } catch (error) {
      console.error('Ошибка обновления токена:', error);
      dispatch({ type: 'AUTH_ERROR' });
    }
  }, []);

  // Если токены были обновлены через axios-interceptor (401->refresh),
  // синхронизируем состояние контекста, чтобы не было "старого refresh" в памяти.
  useEffect(() => {
    const handler = (e) => {
      const next = e?.detail;
      if (!next || !next.access) return;
      dispatch({ type: 'TOKENS_UPDATED', payload: { tokens: next } });
    };
    window.addEventListener('aot_tokens_updated', handler);
    return () => window.removeEventListener('aot_tokens_updated', handler);
  }, []);

  // Синхронизация авторизации между вкладками
  useEffect(() => {
    const handler = (e) => {
      if (!e) return;
      if (e.key === 'tokens') {
        if (e.newValue) {
          try {
            const parsedTokens = JSON.parse(e.newValue);
            const rawUser = localStorage.getItem('user');
            const parsedUser = rawUser ? JSON.parse(rawUser) : null;
            dispatch({
              type: 'LOAD_USER',
              payload: {
                user: parsedUser,
                tokens: parsedTokens,
              },
            });
          } catch (_) {
            // ignore
          }
        } else {
          dispatch({ type: 'AUTH_ERROR' });
        }
      }
      if (e.key === 'user' && e.newValue) {
        try {
          const parsedUser = JSON.parse(e.newValue);
          dispatch({ type: 'UPDATE_USER', payload: parsedUser });
        } catch (_) {
          // ignore
        }
      }
      if (e.key === 'aot_auth_event' && e.newValue) {
        try {
          const rawTokens = localStorage.getItem('tokens');
          if (!rawTokens) return;
          const parsedTokens = JSON.parse(rawTokens);
          const rawUser = localStorage.getItem('user');
          const parsedUser = rawUser ? JSON.parse(rawUser) : null;
          dispatch({
            type: 'LOAD_USER',
            payload: {
              user: parsedUser,
              tokens: parsedTokens,
            },
          });
        } catch (_) {
          // ignore
        }
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Проверка токенов при загрузке приложения
  useEffect(() => {
    const tokens = localStorage.getItem('tokens');
    const user = localStorage.getItem('user');
    
    if (tokens && user) {
      try {
        const parsedTokens = JSON.parse(tokens);
        const parsedUser = JSON.parse(user);
        
        // Проверяем не истек ли токен (безопасное декодирование base64url)
        const tokenData = decodeJwtPayload(parsedTokens.access);
        const currentTime = Date.now() / 1000;
        
        if (tokenData && tokenData.exp > currentTime) {
          dispatch({
            type: 'LOAD_USER',
            payload: {
              user: parsedUser,
              tokens: parsedTokens,
            },
          });
        } else if (tokenData && tokenData.exp <= currentTime) {
          // Токен истек, пытаемся обновить
          refreshToken(parsedTokens.refresh);
        } else {
          // Не смогли декодировать — загружаем как есть (пусть сервер решит)
          dispatch({
            type: 'LOAD_USER',
            payload: {
              user: parsedUser,
              tokens: parsedTokens,
            },
          });
        }
      } catch (error) {
        console.error('Ошибка при восстановлении сессии:', error);
        dispatch({ type: 'AUTH_ERROR' });
      }
    } else {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [refreshToken]);

  // После восстановления сессии подтягиваем актуальный профиль (в т.ч. права/роль),
  // чтобы UI корректно показывал админские возможности без перелогина.
  useEffect(() => {
    if (!state.isAuthenticated) return;
    if (!state.tokens?.access) return;
    let cancelled = false;

    (async () => {
      try {
        const response = await apiService.getUserProfile();
        if (cancelled) return;
        const freshUser = response?.data;
        if (freshUser && typeof freshUser === 'object') {
          dispatch({ type: 'UPDATE_USER', payload: freshUser });
        }
      } catch (_) {
        // Тихо игнорируем: если токен протух/нет доступа, обработает interceptor/логика AUTH_ERROR
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [state.isAuthenticated, state.tokens?.access]);

  const login = useCallback(async (email, password) => {
    try {
      const response = await apiService.login(email, password);
      const payload = {
        user: response.data.user,
        tokens: response.data.tokens,
      };
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: payload,
      });
      return { success: true, data: response.data };
    } catch (error) {
      dispatch({ type: 'AUTH_ERROR' });
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Ошибка входа',
      };
    }
  }, []);

  const register = useCallback(async (userData) => {
    try {
      const response = await apiService.register(userData);
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: response.data,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Ошибка регистрации',
      };
    }
  }, []);

  const logout = useCallback(async (notifyServer = true) => {
    try {
      if (notifyServer && state.tokens?.refresh) {
        await apiService.logout(state.tokens.refresh);
      }
    } catch (error) {
      console.error('Ошибка при выходе:', error);
    } finally {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      dispatch({ type: 'LOGOUT' });
    }
  }, [state.tokens]);

  const inactivityTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const refreshTimerRef = useRef(null);

  const updateUser = useCallback((userData) => {
    dispatch({
      type: 'UPDATE_USER',
      payload: userData,
    });
  }, []);

  useEffect(() => {
    if (!state.isAuthenticated) {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      return;
    }

    const minutesRaw = Number(state.user?.inactivity_timeout_minutes);
    const clampedMinutes = Number.isFinite(minutesRaw) ? Math.min(Math.max(minutesRaw, 5), 720) : 30;
    const inactivityTimeoutMs = clampedMinutes * 60 * 1000;

    const scheduleLogout = () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      inactivityTimerRef.current = setTimeout(() => {
        console.warn('Пользователь не проявлял активность, выполняем выход');
        logout(false);
      }, inactivityTimeoutMs);
    };

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      scheduleLogout();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        handleActivity();
      }
    };

    activityEvents.forEach((event) => window.addEventListener(event, handleActivity, true));
    document.addEventListener('visibilitychange', handleVisibility, true);
    lastActivityRef.current = Date.now();
    scheduleLogout();

    return () => {
      activityEvents.forEach((event) => window.removeEventListener(event, handleActivity, true));
      document.removeEventListener('visibilitychange', handleVisibility, true);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [state.isAuthenticated, state.user?.inactivity_timeout_minutes, logout]);

  useEffect(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (!state.isAuthenticated || !state.tokens?.access || !state.tokens?.refresh) {
      return undefined;
    }

    const payload = decodeJwtPayload(state.tokens.access);
    if (!payload?.exp) {
      return undefined;
    }

    const now = Date.now();
    const expiresAt = payload.exp * 1000;
    const msUntilRefresh = expiresAt - now - 60_000; // Обновляем токен за минуту до истечения
    const timeout = msUntilRefresh <= 0 ? 0 : msUntilRefresh;
    const delay = timeout === 0 ? 0 : Math.max(timeout, 5_000);

    refreshTimerRef.current = setTimeout(() => {
      if (state.tokens?.refresh) {
        refreshToken(state.tokens.refresh);
      }
    }, delay);

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [state.isAuthenticated, state.tokens?.access, state.tokens?.refresh, refreshToken]);

  const value = {
    user: state.user,
    tokens: state.tokens,
    isAuthenticated: state.isAuthenticated,
    loading: state.loading,
    login,
    register,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
